import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload,
  FileText,
  FileSpreadsheet,
  File,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Pencil,
  Trash2,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ColumnMapper } from "@/components/backtest/ColumnMapper";
import { BacktestLayout } from "@/components/backtest/BacktestLayout";
import { parseCSV, parseXLSX, detectColumns, mapRows } from "@/lib/backtest/csvParser";
import { calculateAll, calculateSummary } from "@/lib/backtest/backtestCalculations";
import {
  deduplicateMappedRows,
  formatDeduplicationSummary,
  type DeduplicationResult,
} from "@/lib/backtest/deduplication";
import { parsePaymentTerms } from "@/lib/backtest/paymentTermsParser";
import { useBacktestRuns } from "@/hooks/useBacktestRuns";
import { formatCurrency } from "@/lib/currency";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import type { ColumnMapping, MappedRow } from "@/types/backtest";
import type { ParseBacktestFileResponse } from "@/types/edge-functions";

// ── File type classification ─────────────────────────────────────────────────

type FileMode = "tabular" | "document"; // tabular = CSV/XLSX, document = PDF/other

interface AiExtractedRow {
  invoice_number: string;
  supplier_name: string;
  invoice_date: string;
  invoice_amount: string;
  payment_terms: string;
  actual_payment_date: string;
}

const EMPTY_AI_ROW = (): AiExtractedRow => ({
  invoice_number: "",
  supplier_name: "",
  invoice_date: "",
  invoice_amount: "",
  payment_terms: "",
  actual_payment_date: "",
});

function classifyFile(file: File): FileMode {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (["csv", "txt", "tsv", "xlsx", "xls"].includes(ext)) return "tabular";
  return "document";
}

function FileIcon({ mode, className }: { mode: FileMode; className?: string }) {
  if (mode === "tabular") return <FileSpreadsheet className={className} />;
  return <File className={className} />;
}

// ── Step indicator ────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3;

const TABULAR_STEPS = ["Upload File", "Map Columns", "Preview & Run"];
const DOCUMENT_STEPS = ["Upload File", "Review Extracted Data", "Preview & Run"];

function StepIndicator({ current, mode }: { current: Step; mode: FileMode | null }) {
  const labels = mode === "document" ? DOCUMENT_STEPS : TABULAR_STEPS;
  return (
    <div className="flex items-center gap-0 mb-8">
      {labels.map((label, i) => {
        const n = (i + 1) as Step;
        return (
          <div key={n} className="flex items-center">
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                current === n
                  ? "bg-primary text-primary-foreground"
                  : current > n
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground"
              }`}
            >
              {current > n ? (
                <CheckCircle2 className="w-3.5 h-3.5" />
              ) : (
                <span className="w-4 h-4 rounded-full border border-current flex items-center justify-center text-[10px]">
                  {n}
                </span>
              )}
              {label}
            </div>
            {i < labels.length - 1 && (
              <div className={`h-px w-6 mx-1 ${current > n ? "bg-primary/30" : "bg-border"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Editable AI row table ─────────────────────────────────────────────────────

const AI_FIELDS: { key: keyof AiExtractedRow; label: string; placeholder: string }[] = [
  { key: "supplier_name", label: "Supplier", placeholder: "Acme Corp" },
  { key: "invoice_number", label: "Invoice #", placeholder: "INV-001" },
  { key: "invoice_date", label: "Invoice Date", placeholder: "2024-01-15" },
  { key: "invoice_amount", label: "Amount", placeholder: "5000.00" },
  { key: "payment_terms", label: "Terms", placeholder: "2/10 Net 30" },
  { key: "actual_payment_date", label: "Payment Date", placeholder: "2024-01-20" },
];

function AiRowsEditor({
  rows,
  onChange,
}: {
  rows: AiExtractedRow[];
  onChange: (rows: AiExtractedRow[]) => void;
}) {
  const update = (i: number, key: keyof AiExtractedRow, value: string) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r));
    onChange(next);
  };
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, EMPTY_AI_ROW()]);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-xs min-w-[800px]">
          <thead>
            <tr className="border-b bg-muted/40">
              {AI_FIELDS.map((f) => (
                <th key={f.key} className="text-left py-2 px-2 font-medium text-muted-foreground">
                  {f.label}
                </th>
              ))}
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
                {AI_FIELDS.map((f) => (
                  <td key={f.key} className="px-1.5 py-1">
                    <Input
                      value={row[f.key]}
                      onChange={(e) => update(i, f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className="h-7 text-xs border-0 bg-transparent focus:bg-background focus:border focus:border-border px-1.5"
                    />
                  </td>
                ))}
                <td className="px-1 py-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(i)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={add}>
        <Plus className="w-3 h-3 mr-1" />
        Add Row
      </Button>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function BacktestNewPage() {
  const navigate = useNavigate();
  const { createRun } = useBacktestRuns();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>(1);
  const [isDragging, setIsDragging] = useState(false);

  // Step 1
  const [file, setFile] = useState<File | null>(null);
  const [files, setFiles] = useState<File[]>([]); // multiple files (e.g. PAR Systems: contracts + payment CSV)
  const [fileMode, setFileMode] = useState<FileMode | null>(null);
  const [analysisName, setAnalysisName] = useState("");
  const [clientName, setClientName] = useState(""); // e.g. "PAR Systems"
  const [fileError, setFileError] = useState<string | null>(null);

  // Tabular path (CSV / XLSX)
  const [headers, setHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({
    supplierName: null,
    invoiceDate: null,
    invoiceAmount: null,
    paymentTerms: null,
    actualPaymentDate: null,
    invoiceNumber: null,
  });

  // Document path (PDF / other)
  const [isParsingDoc, setIsParsingDoc] = useState(false);
  const [parseDocError, setParseDocError] = useState<string | null>(null);
  const [aiRows, setAiRows] = useState<AiExtractedRow[]>([]);

  // Step 3 shared
  const [mappedRows, setMappedRows] = useState<MappedRow[]>([]);
  const [dedupResult, setDedupResult] = useState<DeduplicationResult<MappedRow> | null>(null);
  const [progress, setProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── File loading ─────────────────────────────────────────────────────────

  const loadFile = useCallback(async (f: File, opts?: { multiFileList?: File[] }) => {
    setFileError(null);
    setParseDocError(null);
    const mode = classifyFile(f);
    const ext = f.name.split(".").pop()?.toLowerCase() ?? "";

    if (mode === "tabular") {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          let result: { headers: string[]; rows: Record<string, string>[] };
          if (["xlsx", "xls"].includes(ext)) {
            result = parseXLSX(e.target?.result as ArrayBuffer);
          } else {
            result = parseCSV(e.target?.result as string);
          }
          if (result.headers.length === 0) {
            setFileError("Could not parse the file. Ensure it has a header row.");
            return;
          }
          setFile(f);
          setFiles(opts?.multiFileList ?? [f]);
          setFileMode("tabular");
          setHeaders(result.headers);
          setRawRows(result.rows);
          setMapping(detectColumns(result.headers));
          setAnalysisName(
            f.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " ") +
              (opts?.multiFileList && opts.multiFileList.length > 1
                ? ` + ${opts.multiFileList.length - 1} more`
                : "")
          );
        } catch (err) {
          setFileError(err instanceof Error ? err.message : "Failed to parse file.");
        }
      };
      if (["xlsx", "xls"].includes(ext)) {
        reader.readAsArrayBuffer(f);
      } else {
        reader.readAsText(f);
      }
    } else {
      setFile(f);
      setFiles(opts?.multiFileList ?? [f]);
      setFileMode("document");
      setAnalysisName(
        f.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " ") +
          (opts?.multiFileList && opts.multiFileList.length > 1
            ? ` + ${opts.multiFileList.length - 1} more`
            : "")
      );
    }
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const list = e.target.files;
      if (!list?.length) return;
      const arr = Array.from(list);
      if (arr.length > 1) {
        loadFile(arr[0], { multiFileList: arr });
      } else {
        loadFile(arr[0]);
      }
    },
    [loadFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const list = e.dataTransfer.files;
      if (!list.length) return;
      const arr = Array.from(list);
      if (arr.length > 1) loadFile(arr[0], { multiFileList: arr });
      else loadFile(arr[0]);
    },
    [loadFile]
  );

  const clearFile = () => {
    setFile(null);
    setFiles([]);
    setFileMode(null);
    setHeaders([]);
    setRawRows([]);
    setAiRows([]);
  };

  // ── Step 1 → 2 ───────────────────────────────────────────────────────────

  const step1Valid = !!file && analysisName.trim().length > 0;

  const REQUIRED_FIELDS: (keyof ColumnMapping)[] = [
    "supplierName",
    "invoiceDate",
    "invoiceAmount",
    "paymentTerms",
    "actualPaymentDate",
  ];
  const step2TabularValid = REQUIRED_FIELDS.every((f) => !!mapping[f]);
  const step2DocumentValid = aiRows.length > 0;

  const goToStep2 = async () => {
    if (!step1Valid || !file) return;
    setStep(2);

    if (fileMode === "document") {
      setIsParsingDoc(true);
      setParseDocError(null);
      const toProcess = files.length > 1 ? files : [file];
      try {
        const allRows: AiExtractedRow[] = [];
        for (const f of toProcess) {
          const buffer = await f.arrayBuffer();
          const bytes = new Uint8Array(buffer);
          let binary = "";
          const CHUNK = 8192;
          for (let i = 0; i < bytes.length; i += CHUNK) {
            binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
          }
          const base64 = btoa(binary);
          const data = await invokeEdgeFunction<ParseBacktestFileResponse>("parse-backtest-file", {
            fileBase64: base64,
            fileName: f.name,
            mimeType: f.type,
          });
          if (!data || typeof data !== "object") {
            throw new Error(`Invalid response from parse-invoice for ${f.name}`);
          }
          const dataObj = data as Record<string, unknown>;
          if (!dataObj.success)
            throw new Error(
              (dataObj.error as string | undefined) ?? `Extraction failed for ${f.name}`
            );
          const extracted = (data.rows ?? []).map((r) => ({
            invoice_number: r.invoice_number ?? "",
            supplier_name: r.supplier_name ?? "",
            invoice_date: r.invoice_date ?? "",
            invoice_amount: r.invoice_amount != null ? String(r.invoice_amount) : "",
            payment_terms: r.payment_terms ?? "",
            actual_payment_date: r.actual_payment_date ?? "",
          }));
          allRows.push(...extracted);
        }
        setAiRows(allRows.length > 0 ? allRows : [EMPTY_AI_ROW()]);
      } catch (err) {
        setParseDocError(
          err instanceof Error ? err.message : "AI extraction failed. You can enter data manually."
        );
        setAiRows([EMPTY_AI_ROW()]);
      } finally {
        setIsParsingDoc(false);
      }
    }
  };

  // ── Step 2 → 3 ───────────────────────────────────────────────────────────

  const goToStep3 = () => {
    let rows: MappedRow[];
    if (fileMode === "tabular") {
      if (!step2TabularValid) return;
      rows = mapRows(rawRows, mapping);
    } else {
      rows = aiRows.map((r, i) => ({
        rowNumber: i + 1,
        invoiceNumber: r.invoice_number || null,
        supplierName: r.supplier_name || null,
        invoiceDate: r.invoice_date || null,
        invoiceAmount: r.invoice_amount
          ? parseFloat(r.invoice_amount.replace(/[$,\s]/g, "")) || null
          : null,
        paymentTerms: r.payment_terms || null,
        actualPaymentDate: r.actual_payment_date || null,
      }));
    }
    // Deduplicate within this upload (last occurrence wins)
    const dedup = deduplicateMappedRows(rows);
    setDedupResult(dedup);
    setMappedRows(dedup.rows);
    setStep(3);
  };

  // ── Preview stats (step 3) ────────────────────────────────────────────────

  const previewRows = mappedRows.slice(0, 5);
  const withTerms = mappedRows.filter((r) => parsePaymentTerms(r.paymentTerms) !== null).length;
  const withErrors = mappedRows.filter(
    (r) => !r.invoiceDate || !r.actualPaymentDate || r.invoiceAmount == null
  ).length;

  // ── Run analysis ──────────────────────────────────────────────────────────

  const runAnalysis = async () => {
    if (!file) return;
    setIsSubmitting(true);
    setSubmitError(null);
    setProgress(0);
    try {
      const calculated = calculateAll(mappedRows);
      const summary = calculateSummary(calculated);
      const run = await createRun.mutateAsync({
        name: analysisName.trim(),
        clientName: clientName.trim() || null,
        rows: calculated,
        summary,
        file,
        onProgress: setProgress,
      });
      navigate(`/backtest/${run.id}`);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Something went wrong. Please try again."
      );
      setIsSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <BacktestLayout backLabel="All Analyses" backTo="/backtest">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h2 className="text-xl font-display font-bold tracking-tight">New Backtest Analysis</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Accept data from clients (e.g. PAR Systems) in any form — contracts as PDFs, payment
            data as CSV, Excel, or Word. We parse and merge to build a view of how often they took
            the early payment discount and when they missed savings.
          </p>
        </div>

        <StepIndicator current={step} mode={fileMode} />

        {/* ── Step 1: Upload ── */}
        {step === 1 && (
          <div className="space-y-5">
            <div
              className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : file
                    ? "border-green-400 bg-green-50"
                    : "border-border hover:border-primary/50 hover:bg-muted/30"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.tsv,.xlsx,.xls,.pdf,.doc,.docx"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
              {file && fileMode ? (
                <div className="flex flex-col items-center gap-2">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                  <div className="flex items-center gap-2">
                    <FileIcon mode={fileMode} className="w-4 h-4 text-muted-foreground" />
                    <p className="font-medium text-sm">
                      {files.length > 1 ? `${files.length} files` : file.name}
                    </p>
                    <Badge variant="outline" className="text-[10px]">
                      {fileMode === "tabular" ? "Spreadsheet" : "Document — AI will extract data"}
                    </Badge>
                  </div>
                  {fileMode === "tabular" && (
                    <p className="text-xs text-muted-foreground">
                      {rawRows.length.toLocaleString()} rows · {headers.length} columns
                      {files.length > 1 && (
                        <span className="block mt-0.5 text-amber-600">
                          Only the first file is used for tabular data. Multi-CSV merge coming soon.
                        </span>
                      )}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {files.length > 1
                      ? `${(files.reduce((a, f) => a + f.size, 0) / 1024).toFixed(1)} KB total`
                      : `${(file.size / 1024).toFixed(1)} KB`}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 mt-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearFile();
                    }}
                  >
                    Choose different file
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 text-muted-foreground">
                  <Upload className="w-10 h-10" />
                  <div>
                    <p className="font-medium text-sm">Drop your file here, or click to browse</p>
                    <p className="text-xs mt-1">CSV, Excel, PDF, Word — up to 50 MB</p>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {[
                      { icon: FileSpreadsheet, label: "CSV / Excel", sub: "auto column mapping" },
                      { icon: FileText, label: "PDF / Word", sub: "AI data extraction" },
                    ].map(({ icon: Icon, label, sub }) => (
                      <div
                        key={label}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-muted/60 rounded-lg text-left"
                      >
                        <Icon className="w-3.5 h-3.5 shrink-0" />
                        <div>
                          <p className="text-xs font-medium">{label}</p>
                          <p className="text-[10px]">{sub}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {fileError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{fileError}</AlertDescription>
              </Alert>
            )}

            {file && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="client-name">Client / entity name (optional)</Label>
                  <Input
                    id="client-name"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="e.g. PAR Systems"
                    className="h-9"
                  />
                  <p className="text-xs text-muted-foreground">
                    Label this analysis so results show &quot;Early payment performance for
                    [client]&quot;.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="analysis-name">Analysis name</Label>
                  <Input
                    id="analysis-name"
                    value={analysisName}
                    onChange={(e) => setAnalysisName(e.target.value)}
                    placeholder="e.g. Q1 2025 AP Review"
                    className="h-9"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button onClick={goToStep2} disabled={!step1Valid}>
                {fileMode === "document" ? "Extract Data with AI" : "Map Columns"}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2a: Column mapping (tabular) ── */}
        {step === 2 && fileMode === "tabular" && (
          <div className="space-y-5">
            <Card>
              <CardContent className="pt-5 pb-3">
                <p className="text-xs text-muted-foreground mb-4">
                  We auto-detected the columns below. Confirm or adjust each mapping before
                  continuing.
                </p>
                <ColumnMapper headers={headers} mapping={mapping} onChange={setMapping} />
              </CardContent>
            </Card>
            {!step2TabularValid && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  All required fields must be mapped before continuing.
                </AlertDescription>
              </Alert>
            )}
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button onClick={goToStep3} disabled={!step2TabularValid}>
                Preview
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2b: AI extraction review (document) ── */}
        {step === 2 && fileMode === "document" && (
          <div className="space-y-5">
            {isParsingDoc ? (
              <Card>
                <CardContent className="py-12 flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-sm font-medium">Extracting invoice data with AI…</p>
                  <p className="text-xs text-muted-foreground">
                    Analysing <span className="font-medium">{file?.name}</span>
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {parseDocError && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {parseDocError} — You can edit the rows below or add them manually.
                    </AlertDescription>
                  </Alert>
                )}

                {!parseDocError && aiRows.length > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                    <p className="text-xs text-green-800">
                      <span className="font-medium">
                        {aiRows.length} record{aiRows.length !== 1 ? "s" : ""} extracted.
                      </span>{" "}
                      Review and edit below before running the analysis.
                    </p>
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                      <Pencil className="w-3.5 h-3.5" />
                      Extracted Records — click any cell to edit
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {aiRows.length} row{aiRows.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <AiRowsEditor rows={aiRows} onChange={setAiRows} />
                </div>
              </>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} disabled={isParsingDoc}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button onClick={goToStep3} disabled={isParsingDoc || !step2DocumentValid}>
                Preview
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: Preview & Run ── */}
        {step === 3 && (
          <div className="space-y-5">
            {/* Deduplication summary banner */}
            {dedupResult && dedupResult.duplicateCount > 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <span className="font-medium">Duplicates removed: </span>
                  {formatDeduplicationSummary(dedupResult)}. The analysis will use{" "}
                  <span className="font-medium">{dedupResult.rows.length.toLocaleString()}</span>{" "}
                  unique records (latest version of each invoice kept).
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Total Rows", value: mappedRows.length.toLocaleString() },
                {
                  label: "With Discount Terms",
                  value: withTerms.toLocaleString(),
                  sub: `${mappedRows.length > 0 ? ((withTerms / mappedRows.length) * 100).toFixed(0) : 0}% of rows`,
                },
                {
                  label: "Rows with Issues",
                  value: withErrors.toLocaleString(),
                  warning: withErrors > 0,
                },
              ].map((stat) => (
                <Card key={stat.label}>
                  <CardContent className="pt-4 pb-4 text-center">
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
                    {stat.sub && <p className="text-xs text-muted-foreground">{stat.sub}</p>}
                    {stat.warning && stat.value !== "0" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] mt-1 text-yellow-700 border-yellow-300"
                      >
                        will be skipped
                      </Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardContent className="pt-4">
                <p className="text-xs font-medium text-muted-foreground mb-3">
                  Sample rows (first 5)
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        {["Supplier", "Invoice Date", "Amount", "Terms", "Payment Date"].map(
                          (h) => (
                            <th
                              key={h}
                              className="text-left py-1.5 pr-4 font-medium text-muted-foreground"
                            >
                              {h}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((row, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1.5 pr-4 font-medium truncate max-w-32">
                            {row.supplierName ?? "—"}
                          </td>
                          <td className="py-1.5 pr-4 text-muted-foreground">
                            {row.invoiceDate ?? "—"}
                          </td>
                          <td className="py-1.5 pr-4">
                            {row.invoiceAmount != null ? formatCurrency(row.invoiceAmount) : "—"}
                          </td>
                          <td className="py-1.5 pr-4 font-mono">
                            {row.paymentTerms ? (
                              <span
                                className={
                                  parsePaymentTerms(row.paymentTerms)
                                    ? "text-primary"
                                    : "text-muted-foreground"
                                }
                              >
                                {row.paymentTerms}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="py-1.5 text-muted-foreground">
                            {row.actualPaymentDate ?? "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg">
              <FileText className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{file?.name}</span> will be stored
                securely alongside the results so it can be retrieved and verified later.
              </p>
            </div>

            {submitError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}

            {isSubmitting && (
              <div className="space-y-2">
                <Progress value={progress} className="h-1.5" />
                <p className="text-xs text-muted-foreground text-center">
                  {progress < 20
                    ? "Uploading file…"
                    : progress < 30
                      ? "Saving analysis…"
                      : progress < 99
                        ? `Saving rows… ${progress}%`
                        : "Finishing up…"}
                </p>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} disabled={isSubmitting}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button onClick={runAnalysis} disabled={isSubmitting || mappedRows.length === 0}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    Running…
                  </>
                ) : (
                  "Run Analysis"
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </BacktestLayout>
  );
}
